import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "../store";
import {
  NODE_TYPE,
  RFNodeType,
  WorkflowNodeData,
  WorkingDay,
} from "../../pages/DynamicTriggerEngine/components/GraphBuilder/Nodes/utils/types";
import { Edge } from "@xyflow/react";
import { RULE_NODE_TYPES } from "../../pages/DynamicTriggerEngine/components/GraphBuilder/Nodes/utils/constants";
import { TimezoneType } from "../../pages/DynamicTriggerEngine/components/GraphBuilder/Nodes/RuleNodes/Delay/utils/types";
import {
  getLayoutedElements,
  getLayoutedNormalized,
  NodeDimensionsMap,
} from "../../pages/DynamicTriggerEngine/util/layoutEngine";
import type { LeadPathTreeNode } from "../../pages/DynamicTriggerEngine/CreateEditJourney/JourneyAnalytics/utils/types";
import {
  replaceNodeWithApiData,
  deleteNode as deleteNodeFromGraph,
} from "../../pages/DynamicTriggerEngine/util/graphOperations";
import type { NormalizedGraph } from "../../pages/DynamicTriggerEngine/util/normalizedHelpers";
import {
  normalizeGraph,
  denormalizeNodes,
  denormalizeEdges,
} from "../../pages/DynamicTriggerEngine/util/normalizedHelpers";
import { JOURNEY_TYPE } from "../../pages/DynamicTriggerEngine/CreateEditJourney/utils/constants";
import { JOURNEY_STATUS } from "../../pages/DynamicTriggerEngine/AllJourneys/components/JourneyTable/utils/constants";
export interface WorkflowGraphState {
  nodesById: Record<string, RFNodeType>;
  nodeIds: string[];
  edgesById: Record<string, Edge>;
  edgeIds: string[];
  selectedNodeId: string | null;
}

export interface NodePlacementIntent {
  nodeType: NODE_TYPE;
  source: "sidebar" | "drag" | "paste";
}

export interface PendingPanTarget {
  nodeId: string;
}

interface WorkflowUIState {
  placementIntent: NodePlacementIntent | null;
  pendingPanTarget: PendingPanTarget | null;
  actionSplitCreationPending: boolean;
  /** Id of the node copied via "Copy Node" — the paste clipboard. */
  copiedNodeId: string | null;
}

/** Re-exported from the node types, where it lives free of redux imports. */
export { WorkingDay };

export interface WorkingHours {
  timeZone: TimezoneType;
  workingDays: WorkingDay[];
  workingHours: { startTime: string; endTime: string };
}

export interface AIReplyConfig {
  contextRetentionValue: number;
  contextRetentionUnit: string;
  conversationFlowName: string;
}

export interface JourneyDetails {
  journeyId: string;
  title: string;
  description?: string;
  tags?: string[];
  journeyType: JOURNEY_TYPE;
  category: string;
  useCase: string;
  branch?: string;
  status: JOURNEY_STATUS;
  workingHoursConfig?: WorkingHours;
  aiReplyConfig?: AIReplyConfig;
  webhookId?: string | null;
  updatedAt?: string;
  createdAt?: string;
  nodeCount?: number;
}

export interface ScheduledJourneyDetails extends JourneyDetails {
  timezone: string;
  audience_data: {
    audience_count: number;
    audience_data_link: string;
    file_name: string;
    file_key: string;
    file_id: string;
  } | null;
  modality_info: {
    id: string;
    channel: string;
    template: {
      id: string;
      name: string;
    };
    start_date: Date | string;
    start_time: string;
    run_time: string;
    is_saved: boolean;
    is_edited: boolean;
  }[];
  modality: string | null;
}

export interface DynamicTriggerSlice {
  workflowGraphState: WorkflowGraphState;
  workflowUIState: WorkflowUIState;
  journeyDetails: JourneyDetails | ScheduledJourneyDetails;
  nodeDimensions: NodeDimensionsMap;
  isAnalyticsView: boolean;
  /**
   * The path API's `tree`, exactly as it arrives, keyed by node id — set only while the Leads tab's
   * "View path" modal is open. Empty everywhere else, which is what keeps the builder and the Journey
   * Flow tab looking exactly as they do today.
   *
   * Keyed rather than ordered because every node and edge on the canvas asks only about itself: a node
   * reads its own `visited` / `stepNumber` / `dwellMs`, and an edge is answered by the target's own
   * `parentNodeId`. Nothing needs travel order, so nothing computes it.
   */
  leadPathByNodeId: Record<string, LeadPathTreeNode>;
}

const initialState: DynamicTriggerSlice = {
  journeyDetails: {
    journeyId: "",
    title: "",
    description: "",
    tags: [],
    journeyType: JOURNEY_TYPE.DYNAMIC,
    category: "",
    useCase: "",
    branch: "",
    status: JOURNEY_STATUS.DRAFT,
  },
  workflowGraphState: {
    nodesById: {},
    nodeIds: [],
    edgesById: {},
    edgeIds: [],
    selectedNodeId: null,
  },
  workflowUIState: {
    placementIntent: null,
    pendingPanTarget: null,
    actionSplitCreationPending: false,
    copiedNodeId: null,
  },
  nodeDimensions: {},
  isAnalyticsView: false,
  leadPathByNodeId: {},
};

const getGraph = (state: WorkflowGraphState): NormalizedGraph => ({
  nodesById: state.nodesById,
  nodeIds: state.nodeIds,
  edgesById: state.edgesById,
  edgeIds: state.edgeIds,
});

const setGraph = (state: WorkflowGraphState, graph: NormalizedGraph) => {
  state.nodesById = graph.nodesById;
  state.nodeIds = graph.nodeIds;
  state.edgesById = graph.edgesById;
  state.edgeIds = graph.edgeIds;
};

export const dynamicTriggerSlice = createSlice({
  name: "dynamicTrigger",
  initialState,
  reducers: {
    setJourneyDetails: (
      state,
      action: PayloadAction<Partial<JourneyDetails | ScheduledJourneyDetails>>,
    ) => {
      state.journeyDetails = {
        ...state.journeyDetails,
        ...action.payload,
      };
    },

    setNodesAndEdges: (
      state,
      action: PayloadAction<{ nodes: RFNodeType[]; edges: Edge[] }>,
    ) => {
      const { nodes, edges } = getLayoutedElements(
        action.payload.nodes,
        action.payload.edges,
        { nodeDimensions: state.nodeDimensions },
      );
      const normalized = normalizeGraph(nodes, edges);
      setGraph(state.workflowGraphState, normalized);
    },

    setNodes: (state, action: PayloadAction<RFNodeType[]>) => {
      const nodesById: Record<string, RFNodeType> = {};
      const nodeIds: string[] = [];
      for (const node of action.payload) {
        nodesById[node.id] = node;
        nodeIds.push(node.id);
      }
      state.workflowGraphState.nodesById = nodesById;
      state.workflowGraphState.nodeIds = nodeIds;
    },

    updateNodeData: (
      state,
      action: PayloadAction<{
        nodeId: string;
        data: Partial<WorkflowNodeData>;
      }>,
    ) => {
      const { nodeId, data } = action.payload;
      const node = state.workflowGraphState.nodesById[nodeId];
      if (node) {
        node.data = { ...node.data, ...data };
      }
    },

    setNodeLoadingState: (
      state,
      action: PayloadAction<{ nodeId: string; isLoading: boolean }>,
    ) => {
      const { nodeId, isLoading } = action.payload;
      const node = state.workflowGraphState.nodesById[nodeId];
      if (node) {
        node.data.isLoading = isLoading;
      }
    },

    setNodeFromApiByPreviousId: (
      state,
      action: PayloadAction<{
        previousId: string;
        apiNodeData: WorkflowNodeData;
        markAsSaved?: boolean;
        markAsConfigured?: boolean;
      }>,
    ) => {
      const {
        previousId,
        apiNodeData,
        markAsSaved = true,
        markAsConfigured,
      } = action.payload;
      const { graph, selectedNodeId } = replaceNodeWithApiData(
        previousId,
        apiNodeData,
        markAsSaved,
        getGraph(state.workflowGraphState),
        state.workflowGraphState.selectedNodeId,
        markAsConfigured,
      );
      setGraph(state.workflowGraphState, graph);
      state.workflowGraphState.selectedNodeId = selectedNodeId;
    },

    setEdges: (state, action: PayloadAction<Edge[]>) => {
      const edgesById: Record<string, Edge> = {};
      const edgeIds: string[] = [];
      for (const edge of action.payload) {
        edgesById[edge.id] = edge;
        edgeIds.push(edge.id);
      }
      state.workflowGraphState.edgesById = edgesById;
      state.workflowGraphState.edgeIds = edgeIds;
    },

    setWorkingHoursConfig: (
      state,
      action: PayloadAction<WorkingHours | undefined>,
    ) => {
      state.journeyDetails.workingHoursConfig = action.payload;
    },

    setAIReplyConfig: (state, action: PayloadAction<AIReplyConfig>) => {
      state.journeyDetails.aiReplyConfig = action.payload;
    },

    setSelectedNode: (state, action: PayloadAction<string | null>) => {
      const prevId = state.workflowGraphState.selectedNodeId;
      const nextId = action.payload;

      if (prevId && prevId !== nextId) {
        const prevNode = state.workflowGraphState.nodesById[prevId];
        if (prevNode) {
          prevNode.selected = false;
        }
      }
      if (nextId) {
        const nextNode = state.workflowGraphState.nodesById[nextId];
        if (nextNode) {
          nextNode.selected = true;
        }
      }
      state.workflowGraphState.selectedNodeId = nextId;
    },

    setPlacementIntent: (
      state,
      action: PayloadAction<NodePlacementIntent | null>,
    ) => {
      state.workflowUIState.placementIntent = action.payload;
    },

    setCopiedNode: (state, action: PayloadAction<string | null>) => {
      state.workflowUIState.copiedNodeId = action.payload;
    },

    setPendingPanTarget: (
      state,
      action: PayloadAction<PendingPanTarget | null>,
    ) => {
      state.workflowUIState.pendingPanTarget = action.payload;
    },

    setActionSplitCreationPending: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.workflowUIState.actionSplitCreationPending = action.payload;
    },

    updateNodeDimensions: (state, action: PayloadAction<NodeDimensionsMap>) => {
      Object.assign(state.nodeDimensions, action.payload);
    },

    reLayoutGraph: (state) => {
      const nodesArr = denormalizeNodes(
        state.workflowGraphState.nodesById,
        state.workflowGraphState.nodeIds,
      );
      const edgesArr = denormalizeEdges(
        state.workflowGraphState.edgesById,
        state.workflowGraphState.edgeIds,
      );
      const { nodes: layouted } = getLayoutedElements(nodesArr, edgesArr, {
        nodeDimensions: state.nodeDimensions,
      });

      let changed = false;
      for (const next of layouted) {
        const prev = state.workflowGraphState.nodesById[next.id];
        if (
          prev &&
          (prev.position.x !== next.position.x ||
            prev.position.y !== next.position.y)
        ) {
          changed = true;
          break;
        }
      }

      if (changed) {
        for (const next of layouted) {
          state.workflowGraphState.nodesById[next.id] = next;
        }
      }
    },

    markNodesInvalid: (state, action: PayloadAction<string[]>) => {
      for (const id of action.payload) {
        const node = state.workflowGraphState.nodesById[id];
        if (node) {
          node.data.markedInvalid = true;
        }
      }
    },

    clearNodeInvalid: (state, action: PayloadAction<string>) => {
      const node = state.workflowGraphState.nodesById[action.payload];
      if (node) {
        node.data.markedInvalid = false;
      }
    },

    removeNodeFromGraph: (state, action: PayloadAction<string>) => {
      const nodeId = action.payload;
      const node = state.workflowGraphState.nodesById[nodeId];

      const deletedGraph = deleteNodeFromGraph(
        nodeId,
        getGraph(state.workflowGraphState),
        node?.data.nodeType === RULE_NODE_TYPES.CONDITIONAL_SPLIT_BRANCH,
      );
      const layouted = getLayoutedNormalized(deletedGraph, {
        nodeDimensions: state.nodeDimensions,
      });
      setGraph(state.workflowGraphState, layouted);

      if (state.workflowGraphState.selectedNodeId === nodeId) {
        state.workflowGraphState.selectedNodeId = null;
      }
    },

    setAnalyticsView: (state, action: PayloadAction<boolean>) => {
      state.isAnalyticsView = action.payload;
    },

    /**
     * Takes the response's `tree` and indexes it. Pass `[]` to clear the highlight and leave the
     * canvas in its normal state.
     */
    setLeadPath: (state, action: PayloadAction<LeadPathTreeNode[]>) => {
      const byNodeId: Record<string, LeadPathTreeNode> = {};
      for (const node of action.payload ?? []) {
        if (node?.nodeId) byNodeId[node.nodeId] = node;
      }
      state.leadPathByNodeId = byNodeId;
    },

    resetWorkflow: () => initialState,
  },
});

export const {
  setJourneyDetails,
  setNodesAndEdges,
  setNodes,
  setWorkingHoursConfig,
  setAIReplyConfig,
  updateNodeData,
  setNodeLoadingState,
  setNodeFromApiByPreviousId,
  setEdges,
  setSelectedNode,
  setPlacementIntent,
  setCopiedNode,
  setPendingPanTarget,
  setActionSplitCreationPending,
  updateNodeDimensions,
  reLayoutGraph,
  markNodesInvalid,
  clearNodeInvalid,
  removeNodeFromGraph,
  setAnalyticsView,
  setLeadPath,
  resetWorkflow,
} = dynamicTriggerSlice.actions;

export const selectGraphState = (state: RootState) =>
  state.dynamicTrigger.workflowGraphState;

export const selectNodesArray = createSelector([selectGraphState], (graph) =>
  denormalizeNodes(graph.nodesById, graph.nodeIds),
);

export const selectLeadPath = (state: RootState) =>
  state.dynamicTrigger.leadPathByNodeId;

/**
 * Whether the lead travelled this exact edge. The target names the parent it arrived by, so a
 * shortcut between two visited nodes is correctly left un-highlighted, with no ordering involved.
 */
const isEdgeTravelled = (
  leadPath: Record<string, LeadPathTreeNode>,
  source: string,
  target: string,
) => {
  const targetNode = leadPath[target];
  if (!targetNode?.visited || targetNode.parentNodeId !== source) return false;
  return leadPath[source]?.visited === true;
};

/**
 * Every edge out of one split shares the same horizontal corridor, and xyflow gives each edge its
 * own `<svg>` ordered by `zIndex` — so a travelled edge sitting anywhere but last in the array is
 * painted over in grey by the siblings after it, and the highlighted path reads as broken. Lifting
 * the travelled ones a layer up makes their order irrelevant.
 */
const TRAVELLED_EDGE_Z_INDEX = 1;

export const selectEdgesArray = createSelector(
  [selectGraphState, selectLeadPath],
  (graph, leadPath) => {
    const edges = denormalizeEdges(graph.edgesById, graph.edgeIds);
    // No path on screen: hand back the builder's own edges untouched.
    if (Object.keys(leadPath).length === 0) return edges;

    return edges.map((edge) =>
      isEdgeTravelled(leadPath, edge.source, edge.target)
        ? { ...edge, zIndex: TRAVELLED_EDGE_Z_INDEX }
        : edge,
    );
  },
);

export const selectSelectedNode = createSelector([selectGraphState], (graph) =>
  graph.selectedNodeId ? (graph.nodesById[graph.selectedNodeId] ?? null) : null,
);

export const selectNodeById = (state: RootState, nodeId: string) =>
  state.dynamicTrigger.workflowGraphState.nodesById[nodeId] ?? null;

export const selectHasUnconfiguredNodes = createSelector(
  [selectGraphState],
  (graph) => ({
    all: graph.nodeIds.some((id) => !graph.nodesById[id]?.data.isComplete),
    split: graph.nodeIds.some(
      (id) =>
        !graph.nodesById[id]?.data.isComplete &&
        graph.nodesById[id]?.data.nodeType ===
          RULE_NODE_TYPES.CONDITIONAL_SPLIT,
    ),
  }),
);

export const selectHasInvalidNodes = createSelector(
  [selectGraphState],
  (graph) =>
    graph.nodeIds.some((id) => graph.nodesById[id]?.data.markedInvalid),
);

export const selectJourneyDetails = (state: RootState) =>
  state.dynamicTrigger.journeyDetails;

export const selectWorkflowUIState = (state: RootState) =>
  state.dynamicTrigger.workflowUIState;

export const selectPlacementIntent = (state: RootState) =>
  state.dynamicTrigger.workflowUIState.placementIntent;

export const selectCopiedNodeId = (state: RootState) =>
  state.dynamicTrigger.workflowUIState.copiedNodeId;

/** The copied node read live from the graph — null once it no longer exists. */
export const selectCopiedNode = (state: RootState) => {
  const copiedNodeId = state.dynamicTrigger.workflowUIState.copiedNodeId;
  return copiedNodeId
    ? (state.dynamicTrigger.workflowGraphState.nodesById[copiedNodeId] ?? null)
    : null;
};

export const selectPendingPanTarget = (state: RootState) =>
  state.dynamicTrigger.workflowUIState.pendingPanTarget;

export const selectActionSplitCreationPending = (state: RootState) =>
  state.dynamicTrigger.workflowUIState.actionSplitCreationPending;


export const selectIsAnalyticsView = (state: RootState) =>
  state.dynamicTrigger.isAnalyticsView;

/** Whether a lead's path is on screen at all. Memoised, so the map is only measured when it changes. */
export const selectIsLeadPathActive = createSelector(
  selectLeadPath,
  (leadPath) => Object.keys(leadPath).length > 0
);

/**
 * This node's entry in the path, or `undefined` when it isn't in one. Returns the stored object as-is
 * — the same reference every call, exactly like the per-node `analytics` read — so it's safe to use
 * directly from `useSelector`.
 */
export const selectLeadPathNode =
  (nodeId?: string) => (state: RootState): LeadPathTreeNode | undefined =>
    nodeId ? state.dynamicTrigger.leadPathByNodeId[nodeId] : undefined;

/** `isEdgeTravelled` as a selector, for the edge component. */
export const selectIsEdgeTaken =
  (source: string, target: string) => (state: RootState) =>
    isEdgeTravelled(state.dynamicTrigger.leadPathByNodeId, source, target);

export default dynamicTriggerSlice.reducer;
